/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Modal } from "@duxton-web/ui"
import { WarningAltFilled } from "@duxton-web/icons"
import { BulkUploadTaskStatus } from "types/delv-platform-api/bulkUploadTask"
import { useTranslations } from "common/utils/i18n"

export const BULK_UPLOAD_GENERIC_ERROR = "BulkUploadGenericError"

export type BulkUploadGenericError = typeof BULK_UPLOAD_GENERIC_ERROR

type PropTypes = {
  visible: boolean
  error?: BulkUploadTaskStatus | BulkUploadGenericError
  overwriteErrorContent?: {
    // BE sometimes return error message, means FE no need to handle localization
    title?: string
    message?: string
  }
  onCloseCallback: () => void
}

const errorWrapCss = css`
  display: flex;
`

const iconCss = css`
  margin-right: 16px;
  .duxicon {
    color: #d42e1c;
    font-size: 54px;
  }
`

const textCss = css`
  font-size: 20px;
  font-weight: 500;
  color: #000000;
  margin-bottom: 12px;
`

const UploadFailed = ({
  visible,
  error,
  overwriteErrorContent,
  onCloseCallback,
}: PropTypes) => {
  const [errorTitle, errorDescription, genericTitle] = useTranslations([
    `bulkUpload.notifications.${error || BULK_UPLOAD_GENERIC_ERROR}.title`,
    `bulkUpload.notifications.${error ||
      BULK_UPLOAD_GENERIC_ERROR}.description`,
    "bulkUpload.notifications.Failed.title",
  ])
  const finalErrorDescription =
    overwriteErrorContent?.message || errorDescription
  const finalErrorTitle = overwriteErrorContent?.message
    ? overwriteErrorContent?.title || genericTitle
    : errorTitle // title can be overwritten or generic title

  return (
    <Modal
      open={visible}
      footer={null}
      closable
      maskClosable={false}
      onCancel={onCloseCallback}
    >
      <div css={errorWrapCss}>
        <div css={iconCss}>
          <WarningAltFilled />
        </div>
        <div>
          <div css={textCss}>{finalErrorTitle}</div>
          <div
            css={css`
              ${textCss};
              font-size: 16px;
              margin-top: 8px;
            `}
          >
            {finalErrorDescription}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default UploadFailed
